import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import LoadingSpinner from '../common/loading/LoadingSpinner'
import { Pages } from '../../Pages'
import { useAuth } from '../../auth/AuthContext'
import { MAGIC_LINK_ERROR_FLAG } from './constants'

export default function MagicLogin(): React.JSX.Element {
    const navigate = useNavigate()
    const { magicLogin } = useAuth()

    const { token, target } = useMemo(() => {
        const params = new URLSearchParams(window.location.search)
        const t = params.get('token') || undefined
        const targetParam = params.get('target') || undefined
        return {
            token: t,
            target:
                targetParam && targetParam.startsWith('/')
                    ? targetParam
                    : undefined,
        }
    }, [])

    const redirectToLoginWithFlag = useCallback(
        (flag: string, redirectTarget?: string) => {
            const nextParams = new URLSearchParams()
            if (redirectTarget) {
                nextParams.set('target', redirectTarget)
            }
            nextParams.set('magicError', flag)
            const query = nextParams.toString()
            navigate(`${Pages.Login}${query ? `?${query}` : ''}`, {
                replace: true,
            })
        },
        [navigate],
    )

    // Ref guard prevents React StrictMode (dev mode) from double-firing
    // the effect, which would consume the one-time magic link token twice.
    const magicLoginCalled = useRef(false)

    useEffect(() => {
        if (!token) {
            redirectToLoginWithFlag(MAGIC_LINK_ERROR_FLAG, target)
            return
        }

        if (magicLoginCalled.current) return
        magicLoginCalled.current = true
        ;(async () => {
            try {
                await magicLogin(token)
                navigate(target || Pages.Roles, { replace: true })
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (err: unknown) {
                redirectToLoginWithFlag(MAGIC_LINK_ERROR_FLAG, target)
            }
        })()
    }, [token, target, magicLogin, navigate, redirectToLoginWithFlag])

    return <LoadingSpinner />
}
